import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import ReactHighcharts from 'react-highcharts';
import numeral from 'numeral';
import { Dropdown, MenuItem } from 'react-bootstrap';
import {
  reduce, each, capitalize, find, cloneDeep, values,
} from 'lodash';

import ProgressBar from '../../../../components/progress-bar';
import { getColorFromKey } from '../../../../util/helpers';
import { getHasNoDecisionSetup } from '../../../../components/search-form/search-form-reducer';
import { format as formatMoney } from '../../../../components/money-display';
import { getUserCurrentOrganization } from '../../../Auth/AuthReducer';
import { getIsFontSizeBig } from '../../../App/AppReducer';

// Import Selectors
import { fetchOpenStages } from '../../AnalysisActions';
import { getOpportunities, getOpenStages, getOpenStageLoaded as getLoaded, getOpenStageLoading as getLoading } from '../../AnalysisReducer';


const USER_PREFERENCE_RATE_TYPE = 'outcomes-stage-graph-rate-type';
const USER_PREFERENCE_REPORT_TYPE = 'outcomes-stage-graph-report-type';

class StageGraph extends Component {
  constructor(props) {
    super(props);
    this.state = {
      rateType: 'Won', // 'Won' 'Lost', 'No Decision',
      reportType: 'amount', // 'amount', 'count',
      data: [],
    };
    this.setRateType = this.setRateType.bind(this);
    this.setReportType = this.setReportType.bind(this);
  }

  componentWillMount() {
    const { userPreferences } = this.context;
    const rateType = userPreferences[USER_PREFERENCE_RATE_TYPE] || 'Won';
    const reportType = userPreferences[USER_PREFERENCE_REPORT_TYPE] || 'amount';
    this.setState({
      rateType, reportType,
    });
    this.props.dispatch(fetchOpenStages())
      .then(() => { this.calculate(); });
  }
  setRateType(rateType) {
    this.setState({ rateType });
    this.context.updateUserPreference(USER_PREFERENCE_RATE_TYPE, rateType);
  }
  setReportType(reportType) {
    this.setState({ reportType });
    this.context.updateUserPreference(USER_PREFERENCE_REPORT_TYPE, reportType);
  }

  getRateTypeLabel(rateType) {
    if (rateType === 'Won') {
      return 'Win Rate';
    } else if (rateType === 'Lost') {
      return 'Loss Rate';
    }
    return `${rateType} Rate`;
  }
  calculate() {
    const { opportunities, openStages } = this.props;
    const oneStat = {
      count: 0, amount: 0, average: 0, opportunityIds: [],
    };
    const data = reduce(openStages, (items, stage) => {
      items[stage.value] = {
        label: stage.label,
        value: stage.value,
        ...(cloneDeep(oneStat)),
        stages: {
          Won: cloneDeep(oneStat),
          Lost: cloneDeep(oneStat),
          'No Decision': cloneDeep(oneStat),
        },
      };
      return items;
    }, {});
    each(opportunities, (opportunity) => {
      const stageHistories = opportunity.stage_histories;
      const { stage, amount } = opportunity;
      each(stageHistories, (stageHistory) => {
        if (data[stageHistory] && data[stageHistory].stages[stage]) {
          data[stageHistory].count += 1;
          data[stageHistory].amount += amount;
          data[stageHistory].opportunityIds.push(opportunity.opportunity_id);
          data[stageHistory].stages[stage].count += 1;
          data[stageHistory].stages[stage].amount += amount;
          data[stageHistory].stages[stage].opportunityIds.push(opportunity.opportunity_id);
        }
      });
    });
    each(data, (datum) => {
      datum.average = datum.count > 0 ? datum.amount / datum.count : 0;
      each(datum.stages, (stage) => {
        stage.average = stage.count > 0 ? stage.amount / stage.count : 0;
      });
    });
    this.setState({
      data: values(data),
    });
  }
  renderGraph() {
    const { data, reportType, rateType } = this.state;
    const {
      hasNoDecisionSetup, organization,
      isFontSizeBig,
    } = this.props;
    let suffix = '';
    let symbol = '';
    if (organization) {
      suffix = organization.currency_suffix;
      symbol = organization.currency_symbol;
    }
    let yAxisTitle = capitalize(reportType);
    if (reportType === 'amount' && suffix && suffix.length) {
      yAxisTitle += ` (${suffix})`;
    }
    const rateLabel = this.getRateTypeLabel(rateType);
    const pointPlacement = 0;
    const series = [
      {
        name: 'Won', color: getColorFromKey('won'), data: [], pointPlacement,
      },
      {
        name: 'Lost', color: getColorFromKey('lost'), data: [], pointPlacement,
      },
    ];
    if (hasNoDecisionSetup) {
      series.push({
        name: 'No Decision', color: getColorFromKey('noDecision'), data: [], pointPlacement,
      });
    }

    const rateSeries = {
      name: rateLabel,
      color: '#0c7cd5',
      data: [],
      yAxis: 1,
      type: 'line',
      marker: {
        symbol: 'circle',
      },
    };

    const totalSeries = {
      name: 'Total',
      color: '#0c7cd5',
      data: [],
      type: 'column',
      pointPadding: 0,
      pointPlacement: 0,
    };

    const categories = [];
    each(data, (datum) => {
      categories.push({
        label: datum.label,
      });
      const rateSum = datum[reportType] || 0; // amount or count
      let rate = 0;
      if (datum.stages[rateType]) {
        rate = datum.stages[rateType][reportType] || 0;
      }
      const stageStats = reduce(datum.stages, (items, s, n) => {
        let formatedV = s[reportType] || 0;
        if (reportType === 'amount') {
          formatedV = formatMoney(formatedV, true, 1, symbol, suffix);
        } else {
          formatedV = numeral(formatedV).format('0');
        }
        items[n] = formatedV;
        return items;
      }, {});
      rateSeries.data.push({
        isRate: true,
        y: rateSum !== 0 ? (rate / rateSum) * 100 : 0,
        formatedY: `${numeral(rateSum !== 0 ? (rate / rateSum) * 100 : 0).format('0.00')}%`,
        average: formatMoney(datum.average, true, 1, symbol, suffix),
        ...stageStats,
      });
      totalSeries.data.push({
        y: rateSum,
        count: numeral(datum.count).format('0'),
        amount: formatMoney(datum.amount, true, 1, symbol, suffix),
        average: formatMoney(datum.average, true, 1, symbol, suffix),
        opportunityIds: datum.opportunityIds,
        events: {
          click: (event) => {
            this.props.onBarSelect(event.point.options.opportunityIds);
          },
        },
      });
      each(datum.stages, (stageStat, name) => {
        const s = find(series, { name });
        if (s) {
          const value = stageStat[reportType] || 0;
          s.data.push({
            y: value,
            count: numeral(stageStat.count).format('0'),
            opportunityIds: stageStat.opportunityIds,
            amount: formatMoney(stageStat.amount, true, 1, symbol, suffix),
            average: formatMoney(stageStat.average, true, 1, symbol, suffix),
            events: {
              click: (event) => {
                this.props.onBarSelect(event.point.options.opportunityIds);
              },
            },
          });
        }
      });
    });
    series.push(rateSeries);
    series.unshift(totalSeries);
    const wonColor = getColorFromKey('Won');
    const lostColor = getColorFromKey('Lost');
    const noDecisionColor = getColorFromKey('No Decision');
    const rateColor = '#0c7cd5';
    const config = {
      chart: {
        plotBackgroundColor: null,
        plotBorderWidth: null,
        plotShadow: false,
        backgroundColor: 'rgba(0, 0, 0, 0)',
        type: 'column',
        animation: {
          duration: 1000,
        },
      },
      credits: {
        enabled: false,
      },
      title: {
        text: null,
      },
      xAxis: {
        categories,
        labels: {
          formatter: function fx() {
            return this.value.label;
          },
          style: {
            fontSize: isFontSizeBig ? '16px' : null,
          },
        },
        crosshair: true,
      },
      legend: {
        itemStyle: {
          fontSize: isFontSizeBig ? '16px' : null,
        },
      },
      tooltip: {
        useHTML: true,
        backgroundColor: 'rgba(0,0,0, 0.75)',
        borderColor: 'rgba(0,0,0, 0.95)',
        style: {
          color: 'rgba(255, 255, 255, 0.75)',
        },
        formatter: function fmt() {
          const category = this.key.label;
          const { options } = this.point;
          const headerColor = options.isRate ? '#ffffff' : this.series.color;
          let body = '';
          const header = `<thead>
            <tr>
              <td style="color: ${headerColor}" colspan="2"><strong>${category}</strong></td>
            </tr>
          </thead>`;

          if (options.isRate) {
            body = `<tbody>
              <tr>
                <td>Avg deal size</td>
                <td class="text-right">${options.average}</td>
              </tr>
              <tr>
                <td style="color: ${wonColor};">Won</td>
                <td class="text-right" style="color: ${wonColor};">${options.Won}</td>
              </tr>
              <tr>
                <td style="color: ${lostColor};">Lost</td>
                <td class="text-right" style="color: ${lostColor};">${options.Lost}</td>
              </tr>
              <tr>
                <td style="color: ${noDecisionColor};">No Decision</td>
                <td class="text-right" style="color: ${noDecisionColor};">${options['No Decision']}</td>
              </tr>
              <tr>
                <td style="color: ${rateColor};">${this.series.name}</td>
                <td class="text-right" style="color: ${rateColor};">${options.formatedY}</td>
              </tr>
            </tbody>`;
          } else {
            body = `<tbody>
              <tr><td>Total count</td><td class="text-right">${options.count}</td></tr>
              <tr><td>Avg deal size</td><td class="text-right">${options.average}</td></tr>
              <tr><td>Total amount</td><td class="text-right">${options.amount}</td></tr>
            </tbody>`;
          }
          return `<table class="table table-condensed" style="${isFontSizeBig ? 'font-size: 16px;' : ''} margin: 0;">${header}${body}</table>`;
        },
      },
      yAxis: [
        {
          title: {
            text: yAxisTitle,
            style: {
              fontSize: isFontSizeBig ? '16px' : null,
              fontWeight: 'bold',
            },
          },
          allowDecimals: false,
          labels: {
            formatter: function f() {
              let v = 0;
              if (this.value !== 0) {
                if (reportType === 'amount') {
                  v = formatMoney(this.value, true, 1, symbol);
                } else {
                  v = numeral(this.value).format('0');
                }
              }
              return v;
            },
            style: {
              fontSize: isFontSizeBig ? '16px' : null,
            },
          },
          alternateGridColor: 'rgba(66,66,66,0.05)',
          lineWidth: 0,
          plotLines: [{
            value: 0,
            dashStyle: 'dashed',
            color: 'rgba(0, 0, 0, 0.5)',
            width: 2,
            label: {
              text: null,
            },
          }],
        },
        {
          gridLineWidth: 0,
          max: 100,
          min: 0,
          allowDecimals: false,
          title: {
            text: rateLabel,
            style: {
              color: '#0c7cd5',
              fontSize: isFontSizeBig ? '16px' : null,
              fontWeight: 'bold',
            },
          },
          labels: {
            formatter: function f() {
              return `${this.value}%`;
            },
            style: {
              color: '#0c7cd5',
              fontSize: isFontSizeBig ? '16px' : null,
            },
          },
          opposite: true,
        },
      ],
      plotOptions: {
        series: {
          marker: {
            fillColor: '#ffffff',
            lineWidth: 2,
            radius: 5,
            lineColor: null, // inherit from series
          },
        },
      },
      series,
    };

    return (
      <div>
        <ReactHighcharts config={config} isPureConfig />
      </div>
    );
  }
  render() {
    const {
      loaded, loading, opportunities, hasNoDecisionSetup,
    } = this.props;
    if (!loaded || loading || opportunities.length === 0) {
      return <div><ProgressBar /></div>;
    }
    const {
      rateType, reportType,
    } = this.state;
    const stages = [
      { value: 'Won', label: 'Win Rate' },
      { value: 'Lost', label: 'Loss Rate' },
    ];
    if (hasNoDecisionSetup) {
      stages.push({ value: 'No Decision', label: 'No Decision Rate' });
    }
    const activeStage = find(stages, { value: rateType }) || { label: 'Rate' };

    const reportTypes = [
      { value: 'amount', label: 'Amount' },
      { value: 'count', label: 'Count' },
    ];
    const activeReportType = find(reportTypes, { value: reportType }) || { label: 'Amount' };


    return (
      <div>
        <div className="animated fadeIn">
          <div>
            <h5 className="sub-header-with-controls">
              View&nbsp;
              <Dropdown
                id="stage-dropdown"
              >
                <Dropdown.Toggle bsSize="small">
                  {activeStage.label}
                </Dropdown.Toggle>
                <Dropdown.Menu>
                  {stages.map(s => (
                    <MenuItem
                      key={s.value}
                      active={s.value === rateType}
                      onSelect={() => { this.setRateType(s.value); }}
                    >
                      {s.label}
                    </MenuItem>
                    ))}
                </Dropdown.Menu>
              </Dropdown>
              &nbsp;
              <span>for each sales stage by</span>
              &nbsp;
              <Dropdown
                id="report-type-dropdown"
              >
                <Dropdown.Toggle bsSize="small">
                  {activeReportType.label}
                </Dropdown.Toggle>
                <Dropdown.Menu>
                  {reportTypes.map(s => (
                    <MenuItem
                      key={s.value}
                      active={s.value === reportType}
                      onSelect={() => { this.setReportType(s.value); }}
                    >
                      {s.label}
                    </MenuItem>
                    ))}
                </Dropdown.Menu>
              </Dropdown>
            </h5>
          </div>
          {this.renderGraph()}
        </div>
      </div>
    );
  }
}

StageGraph.propTypes = {
  dispatch: PropTypes.func.isRequired,
  opportunities: PropTypes.arrayOf(PropTypes.shape({
    opportunity_id: PropTypes.string.isRequired,
  })).isRequired,
  organization: PropTypes.shape({
    organization_id: PropTypes.string.isRequired,
  }),
  openStages: PropTypes.arrayOf(PropTypes.shape({
    label: PropTypes.string.isRequired,
    value: PropTypes.string.isRequired,
  })).isRequired,
  loaded: PropTypes.bool.isRequired,
  loading: PropTypes.bool.isRequired,
  hasNoDecisionSetup: PropTypes.bool.isRequired,
  isFontSizeBig: PropTypes.bool.isRequired,
  onBarSelect: PropTypes.func,
};

StageGraph.defaultProps = {
  organization: null,
  onBarSelect: () => {},
};

StageGraph.contextTypes = {
  updateUserPreference: PropTypes.func.isRequired,
  userPreferences: PropTypes.object.isRequired,
};

// Retrieve data from store as props
function mapStateToProps(state) {
  return {
    opportunities: getOpportunities(state),
    organization: getUserCurrentOrganization(state),
    openStages: getOpenStages(state),
    loaded: getLoaded(state),
    loading: getLoading(state),
    hasNoDecisionSetup: getHasNoDecisionSetup(state),
    isFontSizeBig: getIsFontSizeBig(state),
  };
}

export default connect(mapStateToProps)(StageGraph);
